import {useEffect, useMemo, useState} from 'react'
import {IconLogout, IconSearch, IconUserCheck, IconUserPlus, IconX} from '@tabler/icons-react'
import {useLocation, useNavigate} from 'react-router-dom'
import {ApiError, isLoggedIn, logout} from './api/client'
import {myHandle, setMyHandle} from './api/me'
import type {NewRole} from './api/relations'
import {
  famsOf, fullName, kidsOf, lifespan, nameMatches, parentsOf, searchWords, shortName, spouseOf,
  loadTree,
} from './api/tree'
import type {TreeIndex} from './api/types'
import {ROLE, whoami} from './api/history'
import HistoryList from './HistoryList'
import LoginScreen from './LoginScreen'
import MediaGrid from './MediaGrid'
import Nav, {type Section} from './Nav'
import NewPersonDialog from './NewPersonDialog'
import PeopleList from './PeopleList'
import PersonPage from './PersonPage'
import PlacesList from './PlacesList'
import Portrait from './Portrait'
import CardMenu from './tree/CardMenu'
import TreeView from './tree/TreeView'
import type {Kin} from './tree/layout'

export default function App() {
  const [authed, setAuthed] = useState(isLoggedIn())
  const nav = useNavigate()
  const loc = useLocation()
  const [ix, setIx] = useState<TreeIndex | null>(null)
  const [err, setErr] = useState('')
  const [kin, setKin] = useState<Kin>('all')
  const [me, setMe] = useState<string | null>(myHandle())
  /* Роль нужна ровно для одного: журнал правок видит только владелец.
     Читать журнал API разрешает всем от Member, так что это защита
     от случайного захода, а не от намеренного. */
  const [role, setRole] = useState(0)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<string | null>(null)
  const [reload, setReload] = useState(0)
  const [adding, setAdding] = useState(false)
  // меню по правому клику на карточке древа и роль, выбранная в нём
  const [menu, setMenu] = useState<{handle: string; x: number; y: number} | null>(null)
  const [newRel, setNewRel] = useState<{role: NewRole; to: string} | null>(null)
  // созданного человека открываем не сразу, а когда древо перечитано:
  // иначе редактор смонтируется на старом индексе и родство в нём будет пустым
  const [pendingEdit, setPendingEdit] = useState<string | null>(null)

  // «я» помнится для каждого логина свой — после смены аккаунта
  // отметку надо перечитать, иначе древо откроется от чужого человека
  useEffect(() => { setMe(myHandle()) }, [authed])

  useEffect(() => {
    if (!authed) { setRole(0); return }
    let alive = true
    whoami().then(u => alive && setRole(u.role)).catch(() => {})
    return () => { alive = false }
  }, [authed])

  useEffect(() => {
    if (!authed) return
    let alive = true
    loadTree()
      .then(t => alive && setIx(t))
      .catch(e => {
        if (!alive) return
        // сессия кончилась по-настоящему — показываем вход, а не окно ошибки
        if (e instanceof ApiError && e.status === 401 && !isLoggedIn()) {
          setAuthed(false)
          return
        }
        setErr(String(e.message || e))
      })
    return () => { alive = false }
  }, [authed, reload])

  /* Раздел и человек в фокусе живут в адресе, а не в состоянии React.
   * Иначе обновление страницы возвращало на стартовый экран и к первому
   * человеку, а ссылку на конкретную ветку некому было бы дать. */
  const personRoute = loc.pathname.startsWith('/person/')
    ? decodeURIComponent(loc.pathname.slice('/person/'.length))
    : ''

  const section: Section = personRoute
    ? 'people'
    : loc.pathname.startsWith('/people')
    ? 'people'
    : loc.pathname.startsWith('/places')
      ? 'places'
      : loc.pathname.startsWith('/media')
        ? 'media'
        : loc.pathname.startsWith('/history')
          ? 'history'
          : 'tree'

  const urlRoot = loc.pathname.startsWith('/tree/') ? decodeURIComponent(loc.pathname.slice(6)) : ''
  /* Без адреса древо открывается от того, кто вошёл: свою родню человек
     ищет в первую очередь. Пока «я» не указан — от самой большой ветки. */
  const fallbackRoot = ix
    ? (me && ix.people.has(me) ? me : ix.components[0]?.[0] || [...ix.people.keys()][0] || '')
    : ''
  // человек из адреса может исчезнуть — тогда молча возвращаемся
  // к умолчанию, а не показываем пустой экран
  const root = ix && urlRoot && ix.people.has(urlRoot) ? urlRoot : fallbackRoot

  const setRoot = (h: string) => nav('/tree/' + encodeURIComponent(h))
  const openPerson = (h: string) => nav('/person/' + encodeURIComponent(h))
  const setSection = (s: Section) =>
    nav(s === 'tree' ? (root ? '/tree/' + encodeURIComponent(root) : '/tree') : '/' + s)

  const hits = useMemo(() => {
    if (!ix || q.trim().length < 2) return []
    const words = searchWords(q)
    return [...ix.people.values()].filter(p => nameMatches(p, words)).slice(0, 8)
  }, [ix, q])

  // созданного человека открываем, когда древо перечитано
  useEffect(() => {
    if (pendingEdit && ix?.people.has(pendingEdit)) {
      nav('/person/' + encodeURIComponent(pendingEdit))
      setPendingEdit(null)
    }
  }, [ix, pendingEdit, nav])

  const exit = () => { logout(); setAuthed(false); setIx(null) }

  if (!authed) return <LoginScreen onDone={() => setAuthed(true)} />

  if (err) {
    return (
      <div className="center">
        <div className="err">
          <b>Не удалось загрузить древо</b>
          <div>{err}</div>
          <button className="btn" onClick={exit}>Войти заново</button>
        </div>
      </div>
    )
  }

  if (!ix || !root) return <div className="center"><div className="spin">Загружаем древо…</div></div>

  const selP = sel ? ix.people.get(sel) : null
  const openInTree = (h: string) => setRoot(h)

  return (
    <>
      <Nav value={section} onChange={setSection} owner={role >= ROLE.owner} />
      <button className="railout" onClick={exit} aria-label="Выйти">
        <IconLogout size={20} stroke={1.7} />
        <span className="tip">Выйти</span>
      </button>

      {section === 'tree' && (
        <div className="topbar">
          <div className="brand">
            Древо
            <span className="sub">· {ix.people.size} персон</span>
          </div>

          <div className="search">
            <IconSearch size={15} className="ico" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Найти человека" />
            {hits.length > 0 && (
              <div className="sresults">
                {hits.map(p => (
                  <div key={p.handle} onClick={() => { setRoot(p.handle); setQ('') }}>
                    {fullName(p)}
                    <span>{lifespan(p)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="spacer" />

          <button className="btn" onClick={() => setAdding(true)}>
            <IconUserPlus size={15} stroke={1.7} /> Добавить человека
          </button>

          {/* «Это я» помнится для каждого аккаунта отдельно: у Gramps Web
              связи «пользователь — персона» нет, держим её у себя. */}
          <button className={'btn' + (me === root ? ' on' : '')}
                  title={me === root ? 'Убрать отметку «это я»' : 'Отметить этого человека собой'}
                  onClick={() => {
                    const next = me === root ? null : root
                    setMyHandle(next)
                    setMe(next)
                  }}>
            <IconUserCheck size={15} stroke={1.7} /> Это я
          </button>

          <div className="seg">
            {([['all', 'Все родственники'], ['blood', 'Кровные']] as [Kin, string][]).map(([k, label]) => (
              <button key={k} className={kin === k ? 'on' : ''} onClick={() => setKin(k)}>{label}</button>
            ))}
          </div>

          {/* Древо не одно: в данных есть несвязанные острова. Без этого
              переключателя до них из интерфейса не добраться в принципе. */}
          {ix.components.length > 1 && (
            <div className="seg islands" title="Несвязанные части древа">
              {ix.components.slice(0, 4).map((c, i) => (
                <button key={i} className={c.includes(root) ? 'on' : ''} onClick={() => setRoot(c[0])}>
                  {i === 0 ? 'Основное' : `Островок ${i}`} <em>{c.length}</em>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {section === 'tree' && (
        <TreeView ix={ix} root={root} kin={kin} me={me} onRoot={setRoot} onOpen={h => setSel(h)}
                  onMenu={(h, x, y) => setMenu({handle: h, x, y})} />
      )}

      {section === 'people' && !personRoute && (
        <PeopleList ix={ix} onEdit={openPerson} onShowInTree={openInTree}
                    onAdd={() => setAdding(true)} />
      )}

      {personRoute && (
        <PersonPage
          key={personRoute}
          ix={ix}
          handle={personRoute}
          onSaved={() => setReload(n => n + 1)}
          onOpenPerson={openPerson}
          onShowInTree={openInTree}
          onBack={() => nav('/people')}
        />
      )}

      {section === 'places' && (
        <PlacesList ix={ix} onChanged={() => setReload(n => n + 1)} onOpenPerson={openInTree} />
      )}

      {section === 'history' && role >= ROLE.owner && (
        <HistoryList ix={ix} onChanged={() => setReload(n => n + 1)} onOpenPerson={openPerson} />
      )}

      {section === 'media' && (
        <MediaGrid ix={ix} onOpenPerson={openPerson} onChanged={() => setReload(n => n + 1)} />
      )}

      {selP && section === 'tree' && (
        <div className="panel">
          <button className="close" onClick={() => setSel(null)} aria-label="Закрыть">
            <IconX size={17} stroke={1.8} />
          </button>
          <div className="phead">
            <Portrait p={selP} size={72} shape="rounded" />
            <div>
              <h2>{fullName(selP)}</h2>
              <div className="muted">{lifespan(selP) || 'даты неизвестны'}</div>
            </div>
          </div>

          <dl>
            {selP.profile?.birth?.date && (
              <>
                <dt>Рождение</dt>
                <dd>{selP.profile.birth.date}{selP.profile.birth.place ? `, ${selP.profile.birth.place}` : ''}</dd>
              </>
            )}
            {selP.profile?.death?.date && (
              <>
                <dt>Смерть</dt>
                <dd>{selP.profile.death.date}{selP.profile.death.place ? `, ${selP.profile.death.place}` : ''}</dd>
              </>
            )}
            <dt>Gramps ID</dt>
            <dd>{selP.gramps_id}</dd>
          </dl>

          <button className="btn primary wide" onClick={() => openPerson(selP.handle)}>
            Открыть карточку
          </button>

          <Rel ix={ix} title="Родители" list={parentsOf(ix, selP.handle)} onGo={setRoot} />
          <Rel ix={ix} title="Супруг(а)" list={[spouseOf(ix, selP.handle)].filter((x): x is string => !!x)} onGo={setRoot} />
          <Rel ix={ix} title="Дети" list={kidsOf(ix, selP.handle)} onGo={setRoot} />
          {famsOf(ix, selP.handle).length > 1 && (
            <div className="warn">Несколько браков — в древе пока показывается первый.</div>
          )}
        </div>
      )}

      {menu && (
        <CardMenu
          ix={ix}
          handle={menu.handle}
          at={{x: menu.x, y: menu.y}}
          onCreate={role => setNewRel({role, to: menu.handle})}
          onOpen={() => openPerson(menu.handle)}
          onRoot={() => setRoot(menu.handle)}
          onClose={() => setMenu(null)}
        />
      )}

      {adding && (
        <NewPersonDialog
          ix={ix}
          onClose={() => setAdding(false)}
          onCreated={h => { setAdding(false); setPendingEdit(h); setReload(n => n + 1) }}
        />
      )}

      {/* Родственника из древа заводят пачками, поэтому в карточку не
          уводим — просто перечитываем древо, и новый человек появляется
          на своём месте. Детали дозаполняются через «Открыть карточку». */}
      {newRel && (
        <NewPersonDialog
          ix={ix}
          relation={newRel}
          onClose={() => setNewRel(null)}
          onCreated={() => { setNewRel(null); setReload(n => n + 1) }}
        />
      )}

    </>
  )
}

function Rel({ix, title, list, onGo}: {ix: TreeIndex; title: string; list: string[]; onGo: (h: string) => void}) {
  if (!list.length) return null
  return (
    <div className="rel">
      <div className="lbl">{title}</div>
      {list.map(h => {
        const p = ix.people.get(h)
        return (
          <button key={h} className="mini" onClick={() => onGo(h)}>
            <Portrait p={p} size={26} />
            <span className="mn">{shortName(p)}</span>
            <span className="md">{lifespan(p)}</span>
          </button>
        )
      })}
    </div>
  )
}
